import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Input, Popconfirm, Space, Spin, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeadset, faPhoneSlash, faRobot, faUser } from '@fortawesome/free-solid-svg-icons';
import styled, { keyframes } from 'styled-components';
import caseService from '../../../services/CaseService';
import { API_BASE_URL } from '../../../constants/api';
import { COLORS } from '../../../constants/colors';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(207, 19, 34, 0.45); }
  70% { box-shadow: 0 0 0 8px rgba(207, 19, 34, 0); }
  100% { box-shadow: 0 0 0 0 rgba(207, 19, 34, 0); }
`;

const LivePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  color: ${(p) => (p.$live ? '#cf1322' : COLORS.textMuted)};
  background: ${(p) => (p.$live ? '#fff1f0' : '#f5f5f5')};

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => (p.$live ? '#cf1322' : '#bfbfbf')};
    animation: ${(p) => (p.$live ? pulse : 'none')} 1.6s infinite;
  }
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 8px;
`;

const Bubble = styled.div`
  max-width: 85%;
  align-self: ${(p) => (p.$ai ? 'flex-start' : 'flex-end')};
  background: ${(p) => (p.$ai ? '#f5f5f7' : '#1a1a1a')};
  color: ${(p) => (p.$ai ? '#111' : '#fff')};
  border-radius: ${(p) => (p.$ai ? '16px 16px 16px 4px' : '16px 16px 4px 16px')};
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.45;
  opacity: ${(p) => (p.$partial ? 0.55 : 1)};
  white-space: pre-wrap;
  word-break: break-word;

  .who {
    font-size: 11px;
    font-weight: 600;
    opacity: 0.7;
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const ROLE_BY_TYPE = { assistant_message: 'assistant', user_message: 'user' };

const toE164Display = (p) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || '');
};

/**
 * Live view of a member's AI results call: streaming transcript plus
 * take-over (transfer to a staff phone) and end-call controls. Shows the
 * saved transcript once the call has ended.
 */
const LiveCallDrawer = ({ open, member, defaultTakeoverNumber, aiName = 'AI', onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [call, setCall] = useState(null);
  const [messages, setMessages] = useState([]);
  const [partial, setPartial] = useState({});
  const [live, setLive] = useState(false);
  const [takeoverNumber, setTakeoverNumber] = useState('');
  const [busy, setBusy] = useState(null);
  const wsRef = useRef(null);
  const endRef = useRef(null);

  const caseId = member?.caseId;

  const loadTranscript = useCallback(async () => {
    if (!caseId) return null;
    const res = await caseService.getCallTranscript(caseId);
    if (!res?.success) throw new Error(res?.error || 'Failed to load the call');
    setCall(res.data);
    setLive(res.data.live);
    if (!res.data.live || res.data.messages.length) setMessages(res.data.messages);
    return res.data;
  }, [caseId]);

  const appendLine = (role, text) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === role && (last.text || '').trim() === (text || '').trim()) return prev;
      return [...prev, { role, text }];
    });
  };

  const connect = useCallback((callSid) => {
    const wsBase = API_BASE_URL.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/voice-calls/transcript?callSid=${encodeURIComponent(callSid)}`);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      if (data.type === 'history') {
        setMessages(data.messages.map((m) => ({ role: ROLE_BY_TYPE[m.type], text: m.text })));
        return;
      }
      const role = ROLE_BY_TYPE[data.type];
      if (role) {
        if (data.partial) {
          setPartial((p) => ({ ...p, [role]: data.text }));
        } else {
          setPartial((p) => ({ ...p, [role]: null }));
          appendLine(role, data.text);
        }
      } else if (data.type === 'call_ended') {
        setLive(false);
        setPartial({});
        // The saved transcript/summary lands a moment after the call ends.
        setTimeout(() => { loadTranscript().catch(() => {}); onChanged?.(); }, 3000);
      }
    };
  }, [loadTranscript, onChanged]);

  useEffect(() => {
    if (!open || !caseId) return undefined;
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setPartial({});
    setTakeoverNumber(toE164Display(defaultTakeoverNumber));
    loadTranscript()
      .then((data) => { if (!cancelled && data?.live && data.callSid) connect(data.callSid); })
      .catch((err) => message.error(err.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [open, caseId, defaultTakeoverNumber, loadTranscript, connect]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, partial]);

  // Fallback if the socket misses the end: the queue's polling sees the call finish.
  const rowStatus = member?.lastCall?.status;
  useEffect(() => {
    if (live && rowStatus && !['initiated', 'ringing', 'in-progress'].includes(rowStatus)) {
      setLive(false);
      setPartial({});
      loadTranscript().catch(() => {});
    }
  }, [rowStatus, live, loadTranscript]);

  const run = async (kind, fn, okText) => {
    setBusy(kind);
    try {
      const res = await fn();
      if (!res?.success) throw new Error(res?.error);
      message.success(okText);
      onChanged?.();
    } catch (err) {
      message.error(err.message || 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  const takeOver = () => run(
    'takeover',
    () => caseService.takeOverCall(caseId, takeoverNumber),
    `Transferring the call to ${takeoverNumber}. Answer that phone to take over.`
  );
  const endCall = () => run('end', () => caseService.endResultsCall(caseId), 'Call ended');

  const lines = [
    ...messages.map((m, i) => ({ ...m, key: `m${i}` })),
    ...['assistant', 'user'].filter((r) => partial[r]).map((r) => ({ role: r, text: partial[r], partial: true, key: `p${r}` }))
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={(
        <Space size={12}>
          <span>{member?.name || 'Call'}</span>
          <LivePill $live={live}><span className="dot" />{live ? 'Live' : 'Ended'}</LivePill>
        </Space>
      )}
      footer={live ? (
        <div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>
            Take over: the AI tells the member it&apos;s connecting them, then the call rings this phone.
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Your phone number"
              value={takeoverNumber}
              onChange={(e) => setTakeoverNumber(e.target.value)}
              prefix={<FontAwesomeIcon icon={faHeadset} style={{ color: COLORS.textMuted, marginRight: 4 }} />}
            />
            <Popconfirm title={`Transfer this call to ${takeoverNumber || 'your phone'}?`} okText="Take over" onConfirm={takeOver}>
              <Button
                type="primary"
                loading={busy === 'takeover'}
                disabled={!takeoverNumber.trim() || !!busy}
                style={{ background: '#1a1a1a', borderColor: '#1a1a1a', fontWeight: 600 }}
              >
                Take over call
              </Button>
            </Popconfirm>
          </Space.Compact>
          <Popconfirm title="End this call for the member?" okText="End call" okButtonProps={{ danger: true }} onConfirm={endCall}>
            <Button
              danger
              type="text"
              icon={<FontAwesomeIcon icon={faPhoneSlash} />}
              loading={busy === 'end'}
              disabled={!!busy}
              style={{ marginTop: 8, paddingLeft: 0 }}
            >
              End call
            </Button>
          </Popconfirm>
        </div>
      ) : null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : (
        <>
          {!live && call?.summary && (
            <div style={{ background: '#E6FAF8', color: COLORS.$deepTiffany, borderRadius: 12, padding: '12px 14px', fontSize: 13, marginBottom: 16 }}>
              <strong>Summary:</strong> {call.summary}
            </div>
          )}
          {lines.length === 0 ? (
            <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '40px 0' }}>
              {live ? 'Waiting for the conversation to start…' : 'No transcript was saved for this call.'}
            </div>
          ) : (
            <Thread>
              {lines.map((l) => (
                <Bubble key={l.key} $ai={l.role === 'assistant'} $partial={l.partial}>
                  <div className="who">
                    <FontAwesomeIcon icon={l.role === 'assistant' ? faRobot : faUser} />
                    {l.role === 'assistant' ? aiName : member?.name?.split(' ')[0] || 'Member'}
                    {l.partial && ' · speaking…'}
                  </div>
                  {l.text}
                </Bubble>
              ))}
              <div ref={endRef} />
            </Thread>
          )}
        </>
      )}
    </Drawer>
  );
};

export default LiveCallDrawer;
